// src/components/ReadjudicacionMasiva/Dashboard/LotesTable.js

import React, { useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    TablePagination
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        backgroundColor: '#fff',
        boxShadow: theme.shadows[2]
    },
    tableHeader: {
        backgroundColor: theme.palette.grey[100]
    },
    headerCell: {
        fontWeight: 700,
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: theme.palette.grey[200]
        }
    },
    tableRow: {
        '&:hover': {
            backgroundColor: theme.palette.grey[50],
            cursor: 'pointer'
        }
    },
    chip: {
        fontWeight: 600,
        fontSize: '0.75rem'
    },
    estadoChipPendiente: {
        backgroundColor: '#fff3e0',
        color: '#ff9800'
    },
    estadoChipAuditado: {
        backgroundColor: '#e3f2fd',
        color: '#2196f3'
    },
    estadoChipDesapoderado: {
        backgroundColor: '#f3e5f5',
        color: '#9c27b0'
    },
    estadoChipAsignado: {
        backgroundColor: '#e8f5e9',
        color: '#4caf50'
    },
    estadoChipPasaje: {
        backgroundColor: '#ffebee',
        color: '#f44336'
    },
    estadoChipFinalizado: {
        backgroundColor: '#e0f2f1',
        color: '#009688'
    },
    correctos: {
        color: '#4caf50',
        fontWeight: 600
    },
    defectuosos: {
        color: '#f44336',
        fontWeight: 600
    },
    pendientes: {
        color: '#ff9800',
        fontWeight: 600
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1)
    }
}));

const LotesTable = ({ lotes, onVer, onEliminar, puedeEliminar, totalLotes, page, pageSize, onPageChange, onPageSizeChange }) => {
    const classes = useStyles();
    const [orderBy, setOrderBy] = useState('Fecha');
    const [order, setOrder] = useState('desc');

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getEstadoChip = (estado) => {
        const estados = {
            0: { label: 'Pendiente Auditoría', className: classes.estadoChipPendiente },
            1: { label: 'Auditado', className: classes.estadoChipAuditado },
            2: { label: 'Desapoderado', className: classes.estadoChipDesapoderado },
            3: { label: 'Asignado', className: classes.estadoChipAsignado },
            4: { label: 'Listo para Pasaje', className: classes.estadoChipPasaje },
            5: { label: 'Finalizado', className: classes.estadoChipFinalizado }
        };

        const estadoInfo = estados[estado] || { label: 'Desconocido', className: '' };
        return (
            <Chip
                label={estadoInfo.label}
                size="small"
                className={`${classes.chip} ${estadoInfo.className}`}
            />
        );
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';

        try {
            // Parsear formato .NET JSON: "/Date(1634785200000)/"
            const match = fecha.toString().match(/\/Date\((\d+)\)\//);
            if (match) {
                const timestamp = parseInt(match[1], 10);
                return new Date(timestamp).toLocaleDateString('es-AR');
            }

            // Si no es formato .NET, intentar parsear normalmente
            return new Date(fecha).toLocaleDateString('es-AR');
        } catch {
            return '-';
        }
    };

    // Ordenar lotes
    const sortedLotes = [...lotes].sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Manejo especial para campos numéricos
        if (['TotalRegistros', 'Correctos', 'Defectuosos', 'Pendientes', 'Estado'].includes(orderBy)) {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
        }

        // Manejo especial para fechas
        if (orderBy === 'Fecha') {
            const getTimestamp = (fecha) => {
                if (!fecha) return 0;
                const match = fecha.toString().match(/\/Date\((\d+)\)\//);
                return match ? parseInt(match[1], 10) : new Date(fecha).getTime();
            };
            aValue = getTimestamp(a.Fecha);
            bValue = getTimestamp(b.Fecha);
        }

        // Comparación
        if (order === 'asc') {
            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
    });

    return (
        <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
                <TableHead className={classes.tableHeader}>
                    <TableRow>
                        <TableCell
                            className={classes.headerCell}
                            onClick={() => handleRequestSort('Id')}
                        >
                            ID {orderBy === 'Id' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            onClick={() => handleRequestSort('Expediente')}
                        >
                            Expediente {orderBy === 'Expediente' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            onClick={() => handleRequestSort('Estado')}
                        >
                            Estado {orderBy === 'Estado' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            onClick={() => handleRequestSort('Modulo')}
                        >
                            Módulo {orderBy === 'Modulo' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            onClick={() => handleRequestSort('Fecha')}
                        >
                            Fecha {orderBy === 'Fecha' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            align="center"
                            onClick={() => handleRequestSort('TotalRegistros')}
                        >
                            Total {orderBy === 'TotalRegistros' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            align="center"
                            onClick={() => handleRequestSort('Correctos')}
                        >
                            Correctos {orderBy === 'Correctos' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            align="center"
                            onClick={() => handleRequestSort('Defectuosos')}
                        >
                            Defectuosos {orderBy === 'Defectuosos' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell
                            className={classes.headerCell}
                            align="center"
                            onClick={() => handleRequestSort('Pendientes')}
                        >
                            Pendientes {orderBy === 'Pendientes' && (order === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell className={classes.headerCell} align="center">Acciones</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedLotes.map((lote, idx) => (
                        <TableRow
                            key={lote.Id}
                            className={classes.tableRow}
                            onClick={() => onVer && onVer(lote)}
                            sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}
                        >
                            <TableCell>
                                <strong>{lote.Id}</strong>
                            </TableCell>
                            <TableCell>{lote.Expediente || '-'}</TableCell>
                            <TableCell>{getEstadoChip(lote.Estado)}</TableCell>
                            <TableCell>{lote.Modulo}</TableCell>
                            <TableCell>{formatFecha(lote.Fecha)}</TableCell>
                            <TableCell align="center">{lote.TotalRegistros || 0}</TableCell>
                            <TableCell align="center" className={classes.correctos}>
                                {lote.Correctos || 0}
                            </TableCell>
                            <TableCell align="center" className={classes.defectuosos}>
                                {lote.Defectuosos || 0}
                            </TableCell>
                            <TableCell align="center" className={classes.pendientes}>
                                {lote.Pendientes || 0}
                            </TableCell>
                            <TableCell align="center">
                                <div className={classes.actions}>
                                    <Tooltip title="Ver">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onVer && onVer(lote);
                                            }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {puedeEliminar && lote.Estado < 5 && (
                                        <Tooltip title="Eliminar">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEliminar && onEliminar(lote);
                                                }}
                                                style={{ color: '#f44336' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {totalLotes > 0 && onPageChange && (
                <TablePagination
                    component="div"
                    count={totalLotes}
                    page={(page || 1) - 1}
                    onPageChange={onPageChange}
                    rowsPerPage={pageSize || 25}
                    onRowsPerPageChange={onPageSizeChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            )}
        </TableContainer>
    );
};

export default LotesTable;
